'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { EyePosition, MousePosition } from '@/types';

export const useEyeTracker = () => {
  const [mousePosition, setMousePosition] = useState<MousePosition>({ x: 0, y: 0 });
  const [eyePositions, setEyePositions] = useState<EyePosition[]>([
    { x: 36, y: 36, pupilX: 0, pupilY: 0 },
    { x: 108, y: 36, pupilX: 0, pupilY: 0 }
  ]);
  const [isBlinking, setIsBlinking] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const blinkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Calculate pupil position based on mouse position
  const calculatePupilPosition = useCallback((
    eyeCenterX: number,
    eyeCenterY: number,
    mouseX: number,
    mouseY: number
  ) => {
    // Calculate mouse position relative to SVG center (matching v1 logic)
    const deltaX = mouseX - eyeCenterX;
    const deltaY = mouseY - eyeCenterY;

    // Calculate angle and distance like v1
    const angle = Math.atan2(deltaY, deltaX);
    const distance = Math.min(Math.sqrt(deltaX * deltaX + deltaY * deltaY), 60);

    // Eye movement constraints (pupils can move within the eye bounds) - matching v1
    const maxMovement = 8; // Max pixels the pupil can move from center
    const movementX = Math.cos(angle) * Math.min(distance / 10, maxMovement);
    const movementY = Math.sin(angle) * Math.min(distance / 10, maxMovement);

    return {
      pupilX: movementX,
      pupilY: movementY
    };
  }, []);

  // Update eye positions based on mouse movement
  const updateEyePositions = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return;

    // Get the SVG container position (matching v1 logic)
    const svg = containerRef.current.querySelector('.dynamic-eyes') as SVGElement;
    if (!svg) return;

    const svgRect = svg.getBoundingClientRect();
    const svgCenterX = svgRect.left + svgRect.width / 2;
    const svgCenterY = svgRect.top + svgRect.height / 2;

    // Eye centers in SVG coordinates (matching v1: left eye at 36,36 and right eye at 108,36)
    const leftEyeCenter = { x: svgCenterX - 36, y: svgCenterY }; // Offset from SVG center
    const rightEyeCenter = { x: svgCenterX + 36, y: svgCenterY }; // Offset from SVG center

    const leftPupil = calculatePupilPosition(leftEyeCenter.x, leftEyeCenter.y, clientX, clientY);
    const rightPupil = calculatePupilPosition(rightEyeCenter.x, rightEyeCenter.y, clientX, clientY);

    setEyePositions([
      { x: 36, y: 36, ...leftPupil }, // Left eye SVG coordinates
      { x: 108, y: 36, ...rightPupil } // Right eye SVG coordinates
    ]);

    setMousePosition({ x: clientX, y: clientY });
  }, [calculatePupilPosition]);

  // Automatic blinking (matching v1 timing)
  const triggerBlink = useCallback(() => {
    setIsBlinking(true);
    setTimeout(() => {
      setIsBlinking(false);
    }, 100); // Quick blink like v1 (0.1s)
  }, []);

  // Schedule random blinks (matching v1: 2-5 seconds)
  const scheduleNextBlink = useCallback(() => {
    const randomDelay = Math.random() * 3000 + 2000; // 2-5 seconds

    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current);
    }

    blinkTimeoutRef.current = setTimeout(() => {
      triggerBlink();
      scheduleNextBlink();
    }, randomDelay);
  }, [triggerBlink]);

  // Manual blink trigger
  const manualBlink = useCallback(() => {
    triggerBlink();
    // Reset auto-blink timer
    scheduleNextBlink();
  }, [triggerBlink, scheduleNextBlink]);

  // Mouse move handler with throttling
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      updateEyePositions(e.clientX, e.clientY);
    });
  }, [updateEyePositions]);

  // Setup mouse tracking and blinking
  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      scheduleNextBlink();
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);

      if (blinkTimeoutRef.current) {
        clearTimeout(blinkTimeoutRef.current);
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleMouseMove, scheduleNextBlink]);

  return {
    eyePositions,
    mousePosition,
    isBlinking,
    manualBlink,
    containerRef
  };
};
